import { getTurnstileSiteKey, getValueFromConfigWithOverrides } from "@app/config";
import { useConfigContext } from "@entities/game/model/useConfigContext.ts";
import { LoginViewForContext } from "@features/authV2";
import { ContextModalProps } from "@mantine/modals";
import { useWebPlayerUiStore } from "@pages/WebPlayerPage/store/webPlayerStore.ts";
import * as Analytics from "@shared/lib/Analytics";
import { useEffect } from "react";

import { LoginProvider } from "../lib/types.ts";

const DEFAULT_AUTHENTICATED_EMAIL_TIMEOUT = 1000;

/**
 * @interface AuthLoginModalViewInnerProps
 * @description Пропсы для модального окна входа.
 */
export interface AuthLoginModalViewInnerProps {
	onAuthenticated?: () => void;
	/**
	 * Задержка в миллисекундах перед вызовом onAuthenticated после успешной отправки email.
	 */
	onAuthenticatedEmailTimeout?: number;
}

/**
 * Компонент-обертка для модального окна входа.
 */
export const AuthLoginModalView = (props: ContextModalProps<AuthLoginModalViewInnerProps>) => {
	const {
		platformData: { adapter },
		portalConfig,
		gameConfig
	} = useConfigContext();
	const fxIdInstance = useWebPlayerUiStore((state) => state.fxidInstance);

	/**
	 * При монтировании (открытии) этого модального окна, мы отключаем захват ввода с клавиатуры игрой,
	 * а при размонтировании (закрытии) - включаем обратно.
	 * Это необходимо, чтобы пользователь мог вводить данные в поля формы.
	 */
	useEffect(() => {
		log.debug("AuthLoginModalView mounted, disabling keyboard capture");
		fxIdInstance?.DispatchDisableKeyboardInputCapture();

		return () => {
			log.debug("AuthLoginModalView unmounted, enabling keyboard capture");
			fxIdInstance?.DispatchEnableKeyboardInputCapture();
		};
	}, []); // Пустой массив зависимостей гарантирует выполнение только при монтировании/размонтировании

	// Получаем URL для "Условий использования"
	const currentTosPath =
		getValueFromConfigWithOverrides<string>(
			portalConfig,
			"ExternalTosUrl",
			"ExternalTosUrl",
			adapter,
			gameConfig
		) ??
		gameConfig?.TosUrl ??
		"/docs/tos";

	// Получаем URL для "Политики конфиденциальности"
	const currentPpPath =
		getValueFromConfigWithOverrides<string>(portalConfig, "ExternalPpUrl", "ExternalPpUrl", adapter, gameConfig) ??
		gameConfig?.PpUrl ??
		"/docs/pp";

	// Получаем список разрешенных провайдеров из конфигурации
	const allowedProviders =
		gameConfig?.PublicWebClientConfig.Auth?.AllowedAuthProviders ??
		portalConfig.PortalPublicWebConfig.CompanyBranding.Auth.AllowedAuthProviders;

	const vkOptions = gameConfig?.Auth?.VkontakteComVkId;

	const turnstileSiteKey = getTurnstileSiteKey(portalConfig, gameConfig) ?? "3x00000000000000000000FF";
	const gameSystemName = gameConfig?.GameSystemName;
	const onAuthenticated = props.innerProps?.onAuthenticated;
	const onAuthenticatedEmailTimeout =
		props.innerProps?.onAuthenticatedEmailTimeout ?? DEFAULT_AUTHENTICATED_EMAIL_TIMEOUT;

	void Analytics.site.openLoginModal({ game: gameConfig?.GameSystemName ?? "", adapter });

	return (
		<LoginViewForContext
			{...props}
			innerProps={{
				...props.innerProps,
				turnstileSiteKey,
				gameSystemName,
				onAuthenticated,
				onAuthenticatedEmailTimeout,
				tosUrl: currentTosPath,
				ppUrl: currentPpPath,
				allowedProviders: allowedProviders as LoginProvider[],
				vkOptions
			}}
		/>
	);
};
